import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Stack,
  Chip,
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  AccessTime as AccessTimeIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { toast } from 'react-toastify';
import DailyCheckIn from '../../models/dailyCheckIn';
import { checkinStatusTranslations } from '../../utils/checkinTranslations';

dayjs.extend(utc);
dayjs.extend(timezone);

const dailyCheckInModel = new DailyCheckIn();

const CheckIn = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [error, setError] = useState(null);

  const getStatus = async () => {
    try {
      setError(null);
      const response = await dailyCheckInModel.fetchStatus();
      const responseData = response.data.data;
      setStatus({
        status: responseData.status,
        check_in_at: responseData.check_in_at,
        check_out_at: responseData.check_out_at,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Không thể tải trạng thái điểm danh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    getStatus();
  }, []);

  const handleCheckIn = async () => {
    setActionLoading(true);
    setActiveAction('checkin');
    try {
      await dailyCheckInModel.checkIn();
      toast.success('Điểm danh thành công!');
      await getStatus();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Điểm danh thất bại.';
      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
      setActiveAction(null);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    setActiveAction('checkout');
    try {
      await dailyCheckInModel.checkOut();
      toast.success('Tan làm thành công!');
      await getStatus();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Tan làm thất bại.';
      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
      setActiveAction(null);
    }
  };

  const renderStatus = () => {
    if (!status) return null;

    let icon;
    let color;

    switch (status.status) {
      case 'checked_in':
        icon = <CheckCircleOutlineIcon />;
        color = 'success';
        break;
      case 'checked_out':
        icon = <LogoutIcon />;
        color = 'info';
        break;
      case 'not_checked_in':
      default:
        icon = <AccessTimeIcon />;
        color = 'default';
        break;
    }

    return (
      <Chip
        icon={icon}
        label={checkinStatusTranslations[status.status]}
        color={color}
        variant="outlined"
      />
    );
  };

  const isSunday = dayjs().day() === 0;

  return (
    <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
      <CardContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={{ xs: 2, md: 3 }}
            >
              <Stack>
                <Typography gutterBottom variant="h5" component="div">
                  Điểm Danh Hàng Ngày
                </Typography>
                {renderStatus()}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LoginIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    Điểm danh lúc: <strong>{status?.check_in_at ? dayjs.utc(status.check_in_at).tz('Asia/Ho_Chi_Minh').format('HH:mm') : '--:--'}</strong>
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LogoutIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    Tan làm lúc: <strong>{status?.check_out_at ? dayjs.utc(status.check_out_at).tz('Asia/Ho_Chi_Minh').format('HH:mm') : '--:--'}</strong>
                  </Typography>
                </Stack>
              </Stack>
              
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCheckIn}
                  disabled={loading || actionLoading || isSunday || status?.status !== 'not_checked_in'}
                  startIcon={
                    actionLoading && activeAction === 'checkin' ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <LoginIcon />
                    )
                  }
                >
                  Điểm danh
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCheckOut}
                  disabled={loading || actionLoading || isSunday || status?.status !== 'checked_in'}
                  startIcon={
                    actionLoading && activeAction === 'checkout' ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <LogoutIcon />
                    )
                  }
                >
                  Tan làm
                </Button>
              </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
            {isSunday && !error && (
              <Alert severity="warning" sx={{ width: '100%' }}>
                Chức năng điểm danh/tan làm bị vô hiệu hóa vào Chủ Nhật.
              </Alert>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default CheckIn; 